import React, { useMemo } from 'react';
import {
  SelectOption,
  GroupedMultiSelectOptions,
} from '@bbkAdminComponents/select/select';
import { RestOffer } from '@bbkAdminUtils/api-client/rest-offers-api';
import { PlayType } from '@bbkAdminRedux/rtkq/play.slice';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { categorizeOffersForPlay } from '@bbkAdminUtils/offer-categorization';
import { ADD_OFFER_VALUE } from '@bbkAdminComponents/play/play-context';
import { ActionMenuIcons } from '@bbkAdminComponents/action-menu/action-menu-icons';
import icons from '@bbkAdminUtils/icons';
import { useFeatureFlags, FeatureFlagsE } from '@bbkAdminUtils/feature-flags';

export interface EnhancedOfferSelectProps {
  offers: RestOffer[];
  playType: PlayType;
  offerPresentation?: string;
  isMultiBranded: boolean;
  onOfferSelect: (offerId: string) => void;
  onCreateNewOffer: () => void;
  onCrossObjectiveOfferSelect: (offer: RestOffer) => void;
}

export const useEnhancedOfferSelect = ({
  offers,
  playType,
  offerPresentation,
  isMultiBranded,
  onOfferSelect,
  onCreateNewOffer,
  onCrossObjectiveOfferSelect,
}: EnhancedOfferSelectProps) => {
  const isCrossObjectiveOffersEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_PLAY_CROSS_OBJECTIVE_OFFERS
  );

  const categorization = useMemo(() => {
    return categorizeOffersForPlay(
      offers,
      playType as Exclude<PlayType, PlayType.CHURN_DEFLECT>,
      offerPresentation
    );
  }, [offers, playType, offerPresentation]);

  const groupedOptions: GroupedMultiSelectOptions<SelectOption<string>> =
    useMemo(() => {
      const groups: GroupedMultiSelectOptions<SelectOption<string>> = [];

      const matchingOffersOptions: SelectOption<string>[] =
        categorization.matchingOffers.map((offer) => ({
          label: offer.display_name,
          value: offer.id,
        }));

      const createNewOption: SelectOption<string> = {
        label: (
          <div className="tw-flex tw-items-center tw-gap-1">
            <span className="tw-font-semibold tw-text-cbrprimary-500">
              + New offer {isMultiBranded ? '' : 'with Retention AI '}
              {isMultiBranded ? '' : icons.aiStar}
            </span>
          </div>
        ),
        value: ADD_OFFER_VALUE,
      };

      if (
        matchingOffersOptions.length > 0 ||
        !categorization.hasMatchingOffers
      ) {
        groups.push({
          label: '',
          options: [createNewOption, ...matchingOffersOptions],
        });
      }

      if (
        isCrossObjectiveOffersEnabled &&
        categorization.nonMatchingOffers.length > 0
      ) {
        const nonMatchingOffersOptions: SelectOption<string>[] =
          categorization.nonMatchingOffers.map((categorizedOffer) => {
            const getObjectiveDisplayName = (objective: string): string => {
              const objectiveNames: Record<string, string> = {
                UPSELL: 'Expansion',
                ACQUISITION: 'Acquisition',
                RETENTION: 'Retention',
                CHURN_PREVENTION: 'Churn Prevention',
                CHURN_DEFLECT: 'Churn Deflection',
              };
              return objectiveNames[objective] || objective;
            };

            return {
              label: (
                <div className="tw-flex tw-items-center tw-gap-2">
                  <span className="tw-text-xs">
                    {ActionMenuIcons.duplicate}
                  </span>

                  <span className="tw-text-cbrneutral-700">
                    {categorizedOffer.offer.display_name}
                  </span>

                  {categorizedOffer.offer.objective && (
                    <span className="tw-rounded-full tw-bg-cbrneutral-100 tw-px-2 tw-py-1 tw-text-xs tw-text-cbrneutral-600">
                      {getObjectiveDisplayName(
                        categorizedOffer.offer.objective
                      )}
                    </span>
                  )}
                </div>
              ),
              value: categorizedOffer.offer.id,
            };
          });

        groups.push({
          label: (
            <div className="tw-py-1">
              <span className="tw-font-semibold tw-text-cbrneutral-700">
                OTHER OFFERS
              </span>
              <TippyTooltip
                content={
                  <span className="tw-normal-case">
                    These offers have a different objective. Selecting one will
                    create a copy for your current play.
                  </span>
                }
                tippyOptions={{ placement: 'top' }}
              >
                <span className="tw-ml-1 tw-cursor-pointer tw-items-center tw-text-xs tw-text-cbrneutral-600 hover:tw-text-cbrneutral-700">
                  {icons.cbrInfo}
                </span>
              </TippyTooltip>
            </div>
          ),
          options: nonMatchingOffersOptions,
        });
      }

      return groups;
    }, [
      categorization,
      playType,
      isMultiBranded,
      onOfferSelect,
      onCreateNewOffer,
      onCrossObjectiveOfferSelect,
      isCrossObjectiveOffersEnabled,
    ]);

  const handleOptionSelect = (value: string) => {
    if (value === ADD_OFFER_VALUE) {
      onCreateNewOffer();
    } else {
      const crossObjectiveOffer = isCrossObjectiveOffersEnabled
        ? categorization.nonMatchingOffers.find(
            (categorizedOffer) => categorizedOffer.offer.id === value
          )
        : undefined;

      if (crossObjectiveOffer) {
        onCrossObjectiveOfferSelect(crossObjectiveOffer.offer);
      } else {
        onOfferSelect(value);
      }
    }
  };

  return {
    groupedOptions,
    onSelect: handleOptionSelect,
    hasMatchingOffers: categorization.hasMatchingOffers,
    categorization,
  };
};

export default useEnhancedOfferSelect;
